import { withImageVersion } from "./imageVersion";
import { saveOrShareBlob } from "./share";

/**
 * Build a print-resolution PNG of the chosen Angel inside an ornate golden
 * frame and trigger a download. Intended for printing 8×10" / 16×20" at home
 * or sending to a print shop. Rendered entirely client-side via Canvas.
 *
 * Canvas is 1600×2000 (4:5 portrait, ~200 dpi for an 8×10 print). The frame
 * is multi-layered to evoke a gallery picture-frame:
 *
 *   ┌────────────────────────────────────┐   ← outer brass (60 px)
 *   │ ┌────────────────────────────────┐ │   ← inner-shadow line
 *   │ │ ┌────────────────────────────┐ │ │   ← cream mat
 *   │ │ │ ┌────────────────────────┐ │ │ │   ← brass hairline
 *   │ │ │ │                        │ │ │ │
 *   │ │ │ │      [ artwork ]       │ │ │ │
 *   │ │ │ │                        │ │ │ │
 *   │ │ │ └────────────────────────┘ │ │ │
 *   │ │ │           042              │ │ │   ← engraved numeral on mat
 *   │ │ │      Angel Squad           │ │ │   ← tiny wordmark
 *   │ │ └────────────────────────────┘ │ │
 *   │ └────────────────────────────────┘ │
 *   └────────────────────────────────────┘
 */
const W = 1600;
const H = 2000;
const BG = "#0c0b0a";
const BRASS_LIGHT = "#e2c084";
const BRASS = "#cfa052";
const BRASS_DEEP = "#8a6a32";
const MAT = "#f3ecdd";
const MAT_SHADOW = "#d8cdb4";
const INK = "#3a2e1e";

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

function drawBackground(ctx) {
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);
}

function drawFrame(ctx) {
    // Outer brass frame — vertical gradient for a milled, beveled feel.
    const outerGrad = ctx.createLinearGradient(0, 0, W, 0);
    outerGrad.addColorStop(0,    BRASS_DEEP);
    outerGrad.addColorStop(0.5,  BRASS_LIGHT);
    outerGrad.addColorStop(1,    BRASS_DEEP);
    ctx.fillStyle = outerGrad;
    ctx.fillRect(0, 0, W, H);

    // Carved shadow line — separates outer from inner mat
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.lineWidth = 4;
    ctx.strokeRect(60, 60, W - 120, H - 120);

    // Highlight line just inside the shadow
    ctx.strokeStyle = "rgba(255,255,255,0.25)";
    ctx.lineWidth = 1;
    ctx.strokeRect(68, 68, W - 136, H - 136);

    // Cream mat board
    const matX = 80;
    const matY = 80;
    const matW = W - 160;
    const matH = H - 160;
    const matGrad = ctx.createLinearGradient(matX, matY, matX, matY + matH);
    matGrad.addColorStop(0, MAT);
    matGrad.addColorStop(1, MAT_SHADOW);
    ctx.fillStyle = matGrad;
    ctx.fillRect(matX, matY, matW, matH);

    // Mat inner shadow at top — sells the gallery look
    const innerShadow = ctx.createLinearGradient(matX, matY, matX, matY + 20);
    innerShadow.addColorStop(0, "rgba(0,0,0,0.18)");
    innerShadow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = innerShadow;
    ctx.fillRect(matX, matY, matW, 20);
}

function drawArtwork(ctx, img) {
    // Inner brass hairline + artwork window
    const boxX = 200;
    const boxY = 200;
    const boxW = W - 400;     // 1200
    const boxH = H - 720;     // 1280

    // Sunken brass hairline — slightly inset for an embossed feel
    ctx.strokeStyle = BRASS;
    ctx.lineWidth = 3;
    ctx.strokeRect(boxX - 6, boxY - 6, boxW + 12, boxH + 12);
    ctx.strokeStyle = "rgba(0,0,0,0.25)";
    ctx.lineWidth = 1;
    ctx.strokeRect(boxX - 7, boxY - 7, boxW + 14, boxH + 14);

    // Dark mount behind artwork so contained images don't reveal the mat
    ctx.fillStyle = "#15110c";
    ctx.fillRect(boxX, boxY, boxW, boxH);

    // Fit image inside box (contain)
    const ratio = Math.min(boxW / img.width, boxH / img.height);
    const w = img.width * ratio;
    const h = img.height * ratio;
    const x = boxX + (boxW - w) / 2;
    const y = boxY + (boxH - h) / 2;
    ctx.drawImage(img, x, y, w, h);
}

function drawCaption(ctx, angelNumber) {
    // Engraved Angel numeral on the lower mat
    ctx.fillStyle = INK;
    ctx.font = "300 200px 'Cormorant Garamond', Georgia, serif";
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "center";
    ctx.fillText(angelNumber, W / 2, H - 360);

    // Hairline rule under numeral
    ctx.strokeStyle = "rgba(58,46,30,0.4)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 80, H - 320);
    ctx.lineTo(W / 2 + 80, H - 320);
    ctx.stroke();

    // Tiny wordmark + label
    ctx.fillStyle = INK;
    ctx.font = "400 28px 'Outfit', system-ui, sans-serif";
    ctx.fillText("A N G E L   S Q U A D", W / 2, H - 270);

    ctx.fillStyle = "rgba(58,46,30,0.55)";
    ctx.font = "italic 300 32px 'Cormorant Garamond', Georgia, serif";
    ctx.fillText("from the Sixty", W / 2, H - 220);
}

/**
 * Generate and trigger download of a framed print of the angel.
 *
 * @param {object} args
 * @param {object} args.angel
 * @param {"designed"|"sketch"} [args.variant="designed"]
 */
export async function downloadFramedPrint({ angel, variant = "designed" }) {
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    drawBackground(ctx);
    drawFrame(ctx);

    const useSketch = variant === "sketch" && angel.placeholderImageUrl;
    const sourceUrl = useSketch ? angel.placeholderImageUrl : angel.mainImageUrl;
    const sourceVersion = useSketch ? angel.placeholderImageVersion : angel.imageVersion;

    try {
        const img = await loadImage(withImageVersion(sourceUrl, sourceVersion));
        drawArtwork(ctx, img);
    } catch {
        // Render frame + numeral even if artwork fails so the user still
        // gets something instead of a broken download.
        ctx.fillStyle = "#15110c";
        ctx.fillRect(200, 200, W - 400, H - 720);
    }

    drawCaption(ctx, angel.number);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
    if (!blob) throw new Error("Could not build the framed print.");

    const variantTag = variant === "sketch" ? "-sketch" : "";
    const filename = `angel-squad-${angel.number}${variantTag}-print.png`;
    const result = await saveOrShareBlob({
        blob,
        filename,
        mimeType: "image/png",
        title: `Angel ${angel.number} framed print`,
    });
    return { filename, ...result };
}
